import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { LoadingState } from '@shared/types/loading-state';
import { defaultPaginationState, PaginationState } from '@shared/models/pagination-state';
import { Opportunity } from '@shared/models/opportunity';
import { OpportunityFilters } from '@shared/models/opportunity-filters';
import { createQuotesSuccess } from '@features/cart/actions/create-quotes.actions';
import {
  loadOpportunities,
  loadOpportunitiesFailure,
  loadOpportunitiesSuccess,
} from '@features/opportunities/actions/load-opportunities.actions';
import { loadOpportunity, loadOpportunityFailure, loadOpportunitySuccess } from '../actions/load-opportunity.actions';
import {
  updateOpportunity,
  updateOpportunityFailure,
  updateOpportunitySuccess,
} from '@features/opportunities/actions/update-opportunity.actions';
import { setOpportunityFilters } from '../actions/set-opportunity-filters.actions';
import { setPreferredQuoteSuccess } from '@features/quotes/actions/set-preferred-quote.actions';
import { updateOpportunityValues } from '@features/opportunities/actions/update-opportunity-values.actions';

export const featureKey = 'opportunities';

export const opportunitiesAdapter = createEntityAdapter<Opportunity>({
  selectId: (opportunity: Opportunity) => opportunity.attributes.id,
});

export interface State extends EntityState<Opportunity> {
  loadingState: LoadingState;
  paginationState: PaginationState;
  loadingText?: string;
  filters?: OpportunityFilters;
}

export const initialState: State = opportunitiesAdapter.getInitialState({
  loadingState: 'not_loading' as LoadingState,
  paginationState: defaultPaginationState,
});

export const opportunitiesReducer = createReducer(
  initialState,
  on(loadOpportunities, (state, { filters }) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: undefined,
    filters: filters,
  })),
  on(loadOpportunity, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: 'Loading opportunity details...',
  })),
  on(updateOpportunity, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: 'Updating opportunity...',
  })),
  on(loadOpportunitiesSuccess, (state, { opportunities, paginationState }): State => {
    return opportunitiesAdapter.setAll(opportunities, {
      ...state,
      loadingState: 'loaded' as LoadingState,
      paginationState,
    });
  }),
  on(loadOpportunitySuccess, updateOpportunitySuccess, (state, { opportunity }): State => {
    return opportunitiesAdapter.upsertOne(opportunity, {
      ...state,
      loadingState: 'loaded' as LoadingState,
      loadingText: undefined,
    });
  }),
  on(createQuotesSuccess, (state, { opportunity_id }) => ({
    ...state,
    filters: { id: opportunity_id ? [opportunity_id] : undefined },
  })),
  on(setPreferredQuoteSuccess, (state, { quote }) => {
    const opportunity = state.entities[quote.attributes.opportunity_id!];
    if (!opportunity) return state;

    return opportunitiesAdapter.updateOne(
      {
        id: opportunity.id,
        changes: {
          ...opportunity,
          attributes: {
            ...opportunity.attributes,
            client_value: quote.attributes.client_subtotal,
            margin_value: quote.attributes.margin_total,
            billing_value: quote.attributes.billing_subtotal,
          },
        },
      },
      state,
    );
  }),
  on(setOpportunityFilters, (state, { filters }) => ({
    ...state,
    filters,
  })),
  on(updateOpportunityValues, (state, { opportunityId, quotes }) => {
    const opportunity = state.entities[opportunityId];
    if (!opportunity) return state;

    const processedQuotes = quotes.filter(
      (quote) =>
        quote.attributes.quote_status === 'processed' &&
        ['processed', 'suspended'].includes(quote.attributes.order_status!),
    );
    const preferredQuote = quotes.find((quote) => quote.attributes.preferred);

    const clientValue = processedQuotes.reduce((sum, quote) => sum + Number(quote.attributes.client_subtotal), 0);
    const marginValue = processedQuotes.reduce((sum, quote) => sum + Number(quote.attributes.margin_total), 0);
    const billingValue = processedQuotes.reduce((sum, quote) => sum + Number(quote.attributes.billing_subtotal), 0);

    return opportunitiesAdapter.updateOne(
      {
        id: opportunity.id,
        changes: {
          ...opportunity,
          attributes: {
            ...opportunity.attributes,
            opportunity_stage: processedQuotes.length ? 'won' : 'lost',
            client_value: processedQuotes.length ? clientValue : preferredQuote!.attributes.client_subtotal!,
            margin_value: processedQuotes.length ? marginValue : preferredQuote!.attributes.margin_total!,
            billing_value: processedQuotes.length ? billingValue : preferredQuote!.attributes.billing_subtotal!,
          },
        },
      },
      state,
    );
  }),
  on(loadOpportunitiesFailure, loadOpportunityFailure, updateOpportunityFailure, (state) => ({
    ...state,
    loadingState: 'not_loading' as LoadingState,
    loadingText: undefined,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return opportunitiesReducer(state, action);
}

export const opportunitiesFeatureSelector = createFeatureSelector<State>(featureKey);

const { selectAll, selectEntities } = opportunitiesAdapter.getSelectors(opportunitiesFeatureSelector);

export const selectAllOpportunities = selectAll;

export const selectOpportunitiesLoadingState = createSelector(
  opportunitiesFeatureSelector,
  (state) => state.loadingState,
);

export const selectOpportunitiesLoadingText = createSelector(opportunitiesFeatureSelector, (state: State) => {
  return state.loadingText;
});

export const selectOpportunitiesPaginationState = createSelector(
  opportunitiesFeatureSelector,
  (state) => state.paginationState,
);

export const selectOpportunityFilters = createSelector(opportunitiesFeatureSelector, (state) => state.filters);

export const selectOpportunityById = (opportunityId: number) =>
  createSelector(selectEntities, (entities) => (opportunityId == undefined ? undefined : entities[opportunityId]));
